import * as React from 'react';
import { Link } from 'react-router-dom';
import TrophyIcon from '@mui/icons-material/EmojiEventsOutlined';
import ReposIcon from '@mui/icons-material/HotelOutlined';
import NoteIcon from '@mui/icons-material/StickyNote2Outlined';

import { listerPlanning, type SeancePlanifiee } from '../Planning/planningApi.ts';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { couleurPourNom, ZoneChip } from '../Allures/couleursZones.tsx';
import { formaterDuree, formaterDureeCourse } from '../Construction/formatDuree.ts';
import { afficherNotation, useAjustementRecup } from '../Construction/ajustementRecup.ts';
import { normaliserAllures } from '../Construction/seanceEngine.ts';
import { typeEffectif, typeSeance } from '../Construction/classification.ts';
import { useVma } from '../Allures/useVma.ts';
import DetailSeancePlanifiee from '../Planning/DetailSeancePlanifiee.tsx';
import { envoyerVersIntervalsIcu, texteIntervalsIcu } from '../Planning/intervalsIcuApi.ts';
import LeverIcon from '@mui/icons-material/AlarmOutlined';
import { lireMatineeEnCache, type Matinee } from '../Matinee/matineeApi.ts';
import { formaterHeure, leverFavori } from '../Matinee/calculMatinee.ts';

// Jour (fuseau local) decale de n jours par rapport a aujourd'hui.
function jourDecale(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
}

// AAAA-MM-JJ.
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function km(m?: number | null): string {
  return m ? `${(m / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km` : '';
}

// Tuiles du jour les unes sous les autres : la page d'accueil place les
// jours (aujourd'hui, demain) cote a cote.
const grille: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  // Les tuiles s'etirent pour remplir la colonne : aujourd'hui et demain ont
  // la meme hauteur, quel que soit le contenu ou le nombre de seances.
  gridAutoRows: '1fr',
  flex: 1,
  gap: 16,
  paddingBottom: 16,
};

const tuile: React.CSSProperties = {
  background: 'var(--fond-tuile)',
  border: '1px solid var(--bord-tuile)',
  borderRadius: 8,
  padding: 14,
  boxShadow: '0 1px 3px rgba(16,24,40,0.05)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const petit: React.CSSProperties = { fontSize: 12, color: 'var(--texte-secondaire)' };
const code: React.CSSProperties = { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-word' };

/**
 * Tuiles des seances posees un jour donne dans le calendrier (page
 * d'accueil) : une tuile par seance, repos ou competition. decalage : 0 pour
 * aujourd'hui, 1 pour demain.
 */
export default function SeancesDuJour({ decalage = 0 }: { decalage?: number }) {
  const { table } = useAllureTable();
  const ajustementRecup = useAjustementRecup();
  const [seances, setSeances] = React.useState<SeancePlanifiee[] | null>(null);
  const [erreur, setErreur] = React.useState<string | null>(null);
  const [vma] = useVma();
  // Detail d'une seance (clic sur sa tuile), et resultat d'un envoi intervals.icu.
  const [detail, setDetail] = React.useState<SeancePlanifiee | null>(null);
  const [message, setMessage] = React.useState<{ ok: boolean; texte: string } | null>(null);
  const envoyer = async (p: SeancePlanifiee) => {
    setDetail(null);
    const { texte, erreur: erreurTexte } = texteIntervalsIcu(p, vma, table, ajustementRecup);
    if (!texte) {
      setMessage({ ok: false, texte: `Envoi impossible : ${erreurTexte ?? 'texte vide'}` });
      return;
    }
    try {
      const maj = await envoyerVersIntervalsIcu(p.id, texte);
      setSeances((liste) => (liste ?? []).map((x) => (x.id === maj.id ? maj : x)));
      setMessage({ ok: true, texte: `« ${maj.nom} » envoyée vers intervals.icu.` });
    } catch (e) {
      setMessage({ ok: false, texte: `Envoi impossible : ${(e as Error).message}` });
    }
  };
  // Heure de lever (scenario favori de la page Heure de lever) : sur la
  // premiere seance du jour qui a une duree.
  const [matinee, setMatinee] = React.useState<Matinee | null>(null);
  React.useEffect(() => {
    lireMatineeEnCache().then(setMatinee).catch(() => setMatinee(null));
  }, []);
  const date = React.useMemo(() => jourDecale(decalage), [decalage]);
  const jour = iso(date);
  const libelleJour = decalage === 0 ? "aujourd'hui" : decalage === 1 ? 'demain' : `le ${date.toLocaleDateString('fr-FR')}`;

  React.useEffect(() => {
    listerPlanning(jour, jour)
      .then((liste: SeancePlanifiee[]) => setSeances([...liste].sort((a, b) => a.ordre - b.ordre)))
      .catch((e: Error) => setErreur(e.message));
  }, [jour]);

  const titreJour = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const notation = (n?: string | null) => (n ? afficherNotation(n, ajustementRecup) : '');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <h3 style={{ textAlign: 'center', margin: '8px 0 12px', textTransform: 'capitalize' }}>
        {libelleJour.charAt(0).toUpperCase() + libelleJour.slice(1)} — {titreJour}
      </h3>
      <div style={grille}>
        {erreur && <div style={tuile}><span style={petit}>Séances de {libelleJour} indisponibles ({erreur})</span></div>}
        {seances === null && !erreur && <div style={tuile}><span style={petit}>Chargement…</span></div>}
        {seances?.length === 0 && (
          <div style={tuile}>
            <div style={{ fontWeight: 600 }}>Aucune séance prévue {libelleJour}</div>
            <Link to="/planning" style={petit}>Planifier dans le calendrier</Link>
          </div>
        )}
        {seances?.map((p) => {
          if (p.categorie === 'NOTE') {
            return (
              <div key={p.id} style={{ ...tuile, borderLeft: '5px solid #f9a825', background: 'var(--fond-note)' }}>
                <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <NoteIcon fontSize="small" /> {p.nom}
                </div>
                {p.notes && <div style={{ ...petit, whiteSpace: 'pre-line' }}>{p.notes}</div>}
              </div>
            );
          }
          if (p.categorie === 'REPOS') {
            return (
              <div key={p.id} style={{ ...tuile, borderLeft: '5px solid #9e9e9e', background: 'var(--fond-neutre)' }}>
                <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ReposIcon fontSize="small" /> Repos
                </div>
                {p.notes && <div style={{ ...petit, whiteSpace: 'pre-line' }}>{p.notes}</div>}
              </div>
            );
          }
          const competition = p.categorie === 'COMPETITION';
          const type = competition ? null : typeSeance(typeEffectif({ ...p, notation: p.notationCorps ?? p.notation }, table));
          const duree = p.dureeTotaleS ? (competition ? formaterDureeCourse(p.dureeTotaleS) : formaterDuree(p.dureeTotaleS)) : '';
          const premiere = seances.find((x) => x.categorie !== 'NOTE' && x.categorie !== 'REPOS' && x.dureeTotaleS);
          const lever = matinee && p === premiere && p.dureeTotaleS ? leverFavori(matinee, Math.round(p.dureeTotaleS / 60)) : null;
          return (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              title="Voir le détail de la séance"
              onClick={() => setDetail(p)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDetail(p);
                }
              }}
              style={{ ...tuile, cursor: 'pointer', borderLeft: `5px solid ${couleurPourNom(p.zonePrincipale, table)}` }}
            >
              <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                {competition && <TrophyIcon fontSize="small" />}
                {normaliserAllures(p.nom)}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--texte)' }}>
                {[duree, km(p.distanceTotaleM)].filter(Boolean).join(' · ')}
              </div>
              {lever && (
                <Link
                  to={`/lever?date=${p.date}&seance=${encodeURIComponent(p.id)}&duree=${Math.round(p.dureeTotaleS! / 60)}`}
                  onClick={(e) => e.stopPropagation()}
                  title="Voir le détail des heures de lever"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--texte)', textDecoration: 'none' }}
                >
                  <LeverIcon fontSize="small" />
                  <span>Lever <strong>{formaterHeure(lever.leverMin)}</strong></span>
                  <span style={petit}>· {lever.scenario.nom}</span>
                </Link>
              )}
              <div style={petit}>
                {[competition ? 'Compétition' : type?.libelle, p.chargeUA ? `${Math.round(p.chargeUA)} UA` : '', p.rpe ? `RPE ${p.rpe}` : '']
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              {p.zonePrincipale && <div><ZoneChip nom={p.zonePrincipale} table={table} /></div>}
              {p.nomModele ? (
                <div style={{ ...petit, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {p.notationEchauffement && <span>Échauffement : <span style={code}>{notation(p.notationEchauffement)}</span></span>}
                  <span>Corps : <span style={code}>{notation(p.notationCorps ?? p.notation)}</span></span>
                  {p.notationRecuperation && <span>Récupération : <span style={code}>{notation(p.notationRecuperation)}</span></span>}
                </div>
              ) : (
                <div style={code}>{notation(p.notation)}</div>
              )}
              {p.notes && <div style={{ ...petit, fontStyle: 'italic' }}>{p.notes}</div>}
              {p.lienCourse && (
                <a href={p.lienCourse} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                  Site de la course ↗
                </a>
              )}
            </div>
          );
        })}
      </div>
      {message && (
        <div
          role="status"
          onClick={() => setMessage(null)}
          style={{ ...petit, color: message.ok ? 'var(--succes)' : 'var(--erreur)', textAlign: 'center', marginTop: -8, marginBottom: 8, cursor: 'pointer' }}
        >
          {message.texte}
        </div>
      )}
      <DetailSeancePlanifiee
        p={detail}
        vma={vma}
        table={table}
        libelleType={detail && detail.categorie !== 'COMPETITION'
          ? typeSeance(typeEffectif({ ...detail, notation: detail.notationCorps ?? detail.notation }, table))?.libelle
          : null}
        ajuster={(n: string) => afficherNotation(n, ajustementRecup)}
        onFermer={() => setDetail(null)}
        onEnvoyer={envoyer}
      />
    </div>
  );
}
